import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import {
  Store as StoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Wallet as WalletIcon,
  LocalShipping as LocalShippingIcon,
  Star as StarIcon,
  SwapVert as SwapVertIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import {
  fetchBranches,
  fetchInactiveBranches,
  deleteBranch,
  fetchBranchCities,
  updateBranchDisplayOrder,
} from '../branchesApi';
import { fetchBranchFilters } from '../api/branchFiltersApi';
import { useTranslation } from 'react-i18next';

const Branches = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchFilters, setBranchFilters] = useState({});
  const [branchCities, setBranchCities] = useState({});
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    loadBranches();
  }, []);

  const loadBranches = async () => {
    setLoading(true);
    try {
      // Fetch both active and inactive for admin
      const [activeData, inactiveData] = await Promise.all([
        fetchBranches(),
        fetchInactiveBranches(),
      ]);

      // Merge and deduplicate by Id
      const allMap = new Map();
      [...(activeData || []), ...(inactiveData || [])].forEach((b) => {
        const id = b.Id || b.id;
        if (!allMap.has(id)) allMap.set(id, b);
      });

      const allBranches = Array.from(allMap.values());

      const sorted = allBranches.sort((a, b) => {
        const orderA = a.DisplayOrder ?? a.displayOrder ?? 999999;
        const orderB = b.DisplayOrder ?? b.displayOrder ?? 999999;
        if (orderA !== orderB) return orderA - orderB;
        return (a.Id || a.id) - (b.Id || b.id);
      });

      setBranches(sorted);
      setHasOrderChanged(false);

      const filtersMap = {};
      const citiesMap = {};

      await Promise.all(
        sorted.map(async (branch) => {
          const branchId = branch.Id || branch.id;
          try {
            const filters = await fetchBranchFilters(branchId);
            filtersMap[branchId] = filters;
          } catch {}
          if (branch.DeliveryCities && branch.DeliveryCities.length > 0) {
            citiesMap[branchId] = branch.DeliveryCities;
          } else {
            try {
              const cities = await fetchBranchCities(branchId);
              citiesMap[branchId] = cities;
            } catch {}
          }
        })
      );

      setBranchFilters(filtersMap);
      setBranchCities(citiesMap);
    } catch (error) {
      console.error('Error loading branches:', error);
      Swal.fire(t('Error'), t('BranchLoadFail'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const updated = [...branches];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setBranches(updated);
    setHasOrderChanged(true);
  };

  const handleMoveDown = (index) => {
    if (index === branches.length - 1) return;
    const updated = [...branches];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setBranches(updated);
    setHasOrderChanged(true);
  };

  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const updated = [...branches];
    const draggedItem = updated[dragItem.current];
    updated.splice(dragItem.current, 1);
    updated.splice(dragOverItem.current, 0, draggedItem);

    dragItem.current = null;
    dragOverItem.current = null;

    setBranches(updated);
    setHasOrderChanged(true);
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      // Match backend expected format: { orders: [{ branchId, displayOrder }] }
      const orders = branches.map((branch, index) => ({
        branchId: branch.Id || branch.id,
        displayOrder: index + 1,
      }));

      await updateBranchDisplayOrder(orders);

      const updatedBranches = branches.map((branch, index) => ({
        ...branch,
        DisplayOrder: index + 1,
        displayOrder: index + 1,
      }));
      setBranches(updatedBranches);
      setHasOrderChanged(false);

      setSnackbar({
        open: true,
        message: t('OrderSavedSuccess'),
        severity: 'success',
      });
    } catch (error) {
      console.error('Error saving order:', error);
      setSnackbar({
        open: true,
        message: t('OrderSaveFail'),
        severity: 'error',
      });
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('Are_You_Sure'),
      text: t('DeleteWarning'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('Yes_Delete_It'),
      cancelButtonText: t('Cancel'),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteBranch(id);
          Swal.fire(t('Deleted'), t('BranchDeletedSuccess'), 'success');
          loadBranches();
        } catch (error) {
          Swal.fire(t('Error'), t('BranchDeleteFail'), 'error');
        }
      }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={t('ManageBranches')}
          subheader={t('ManageBranchesDesc')}
          avatar={<StoreIcon color="primary" fontSize="large" />}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {hasOrderChanged && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={
                    savingOrder ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSaveOrder}
                  disabled={savingOrder}
                  sx={{
                    animation: 'pulse 1.5s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.7 },
                    },
                  }}
                >
                  {t('SaveOrder')}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/admin/branches/add')}
              >
                {t('AddBranch')}
              </Button>
            </Box>
          }
          sx={{
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        />

        {/* Reorder hint banner */}
        {!loading && branches.length > 1 && (
          <Box
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: hasOrderChanged ? '#FFF3E0' : '#F3E5F5',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: 1,
              borderColor: 'divider',
              transition: 'background-color 0.3s',
            }}
          >
            <DragIcon
              sx={{
                color: hasOrderChanged ? '#E65100' : '#7B1FA2',
                fontSize: 20,
              }}
            />
            <Typography
              variant="body2"
              color={hasOrderChanged ? '#E65100' : '#7B1FA2'}
              fontWeight={500}
            >
              {hasOrderChanged
                ? t('UnsavedOrderChanges')
                : t('DragToReorder')}
            </Typography>
          </Box>
        )}

        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 130 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <SwapVertIcon
                          fontSize="small"
                          sx={{ color: '#7B1FA2' }}
                        />
                        {t('Order')}
                      </Box>
                    </TableCell>
                    <TableCell>{t('ID')}</TableCell>
                    <TableCell>{t('Image')}</TableCell>
                    <TableCell>{t('Name')}</TableCell>
                    <TableCell>{t('Phone')}</TableCell>
                    <TableCell>{t('Address')}</TableCell>
                    <TableCell>{t('Status')}</TableCell>
                    <TableCell>{t('DeliveryFee')}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <LocalShippingIcon fontSize="small" />
                        {t('DeliveryCities')}
                      </Box>
                    </TableCell>
                    <TableCell>{t('Description')}</TableCell>
                    <TableCell>{t('Filters')}</TableCell>
                    <TableCell align="right">{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {branches.map((branch, index) => {
                    const branchId = branch.Id || branch.id;
                    const cities = branchCities[branchId] || [];
                    const isFeatured =
                      branch.IsFeatured ?? branch.isFeatured ?? false;

                    return (
                      <TableRow
                        key={branchId}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragEnter={() => handleDragEnter(index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => e.preventDefault()}
                        sx={{
                          cursor: 'grab',
                          '&:active': { cursor: 'grabbing' },
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            bgcolor: isFeatured ? '#FFF3CD' : '#F5F5F5',
                          },
                          ...(isFeatured && {
                            bgcolor: '#FFF8E1',
                          }),
                        }}
                      >
                        {/* Order + Move Buttons */}
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Tooltip title={t('DragToReorder')}>
                              <DragIcon
                                sx={{
                                  color: '#9E9E9E',
                                  cursor: 'grab',
                                  '&:hover': { color: '#7B1FA2' },
                                  fontSize: 20,
                                }}
                              />
                            </Tooltip>

                            <Chip
                              label={`#${index + 1}`}
                              size="small"
                              sx={{
                                bgcolor: hasOrderChanged
                                  ? '#FFF3E0'
                                  : '#F3E5F5',
                                color: hasOrderChanged
                                  ? '#E65100'
                                  : '#7B1FA2',
                                fontWeight: 700,
                                fontSize: 12,
                                minWidth: 36,
                              }}
                            />

                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleMoveUp(index)}
                                disabled={index === 0}
                                sx={{
                                  p: 0,
                                  color:
                                    index === 0 ? '#E0E0E0' : '#7B1FA2',
                                  '&:hover': { bgcolor: '#F3E5F5' },
                                }}
                              >
                                <ArrowUpIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveDown(index)}
                                disabled={index === branches.length - 1}
                                sx={{
                                  p: 0,
                                  color:
                                    index === branches.length - 1
                                      ? '#E0E0E0'
                                      : '#7B1FA2',
                                  '&:hover': { bgcolor: '#F3E5F5' },
                                }}
                              >
                                <ArrowDownIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>

                            {isFeatured && (
                              <Tooltip title={t('FeaturedBranch')}>
                                <StarIcon
                                  sx={{ color: '#FF9800', fontSize: 18 }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>

                        <TableCell>{branchId}</TableCell>
                        <TableCell>
                          {(branch.ImageUrl ||
                            branch.imageUrl ||
                            branch.Image ||
                            branch.image) && (
                            <img
                              src={
                                branch.ImageUrl ||
                                branch.imageUrl ||
                                branch.Image ||
                                branch.image
                              }
                              alt="Branch"
                              style={{
                                width: 60,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 4,
                                border: '1px solid #ccc',
                              }}
                              onDragStart={(e) => e.preventDefault()}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              fontWeight={isFeatured ? 700 : 400}
                            >
                              {branch.Name || branch.name}
                            </Typography>
                            {isFeatured && (
                              <Chip
                                icon={<StarIcon sx={{ fontSize: 14 }} />}
                                label={t('FeaturedBranch')}
                                size="small"
                                sx={{
                                  bgcolor: '#FFF3E0',
                                  color: '#E65100',
                                  fontWeight: 600,
                                  fontSize: 11,
                                  height: 22,
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {branch.PhoneNumber || branch.phoneNumber}
                        </TableCell>
                        <TableCell>
                          {branch.Address || branch.address}
                        </TableCell>
                        <TableCell>
                          <Typography
                            color={
                              branch.IsActive ?? branch.isActive
                                ? 'success.main'
                                : 'error.main'
                            }
                          >
                            {branch.IsActive ?? branch.isActive
                              ? t('Active')
                              : t('Inactive')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(branch.deliveryFeeType ||
                            branch.deliveryfeetype) === 'byKm'
                            ? `${
                                branch.deliveryFeeValue ||
                                branch.deliveryfeevalue
                              } ${t('PerKm')}`
                            : `${
                                branch.deliveryFeeValue ||
                                branch.deliveryfeevalue
                              } ${t('Currency')}`}
                        </TableCell>
                        <TableCell>
                          {cities.length > 0 ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                              }}
                            >
                              {cities.length <= 3 ? (
                                cities.map((city) => (
                                  <Chip
                                    key={city.Id || city.id}
                                    label={city.Name || city.name}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                ))
                              ) : (
                                <>
                                  {cities.slice(0, 2).map((city) => (
                                    <Chip
                                      key={city.Id || city.id}
                                      label={city.Name || city.name}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  ))}
                                  <Tooltip
                                    title={cities
                                      .slice(2)
                                      .map((c) => c.Name || c.name)
                                      .join(', ')}
                                    arrow
                                  >
                                    <Chip
                                      label={`+${cities.length - 2}`}
                                      size="small"
                                      color="default"
                                      variant="filled"
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t('NoCitiesSelected')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {branch.Description ||
                              branch.description ||
                              ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(branchFilters[branchId] || [])
                            .map((f) => f.name || f.Name)
                            .join(', ')}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="info"
                            onClick={() =>
                              navigate(
                                `/admin/wallets/branch/${branchId}`
                              )
                            }
                            title={t('ManageWallets')}
                          >
                            <WalletIcon />
                          </IconButton>
                          <IconButton
                            color="primary"
                            onClick={() =>
                              navigate(
                                `/admin/branches/edit/${branchId}`
                              )
                            }
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(branchId)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {branches.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        {t('NoBranchesFound')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Floating Save Button */}
      {hasOrderChanged && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
          }}
        >
          <Button
            variant="contained"
            color="warning"
            size="large"
            startIcon={
              savingOrder ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSaveOrder}
            disabled={savingOrder}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              boxShadow: 6,
              fontWeight: 700,
              fontSize: 15,
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.03)' },
              },
            }}
          >
            {t('SaveOrder')} ({branches.length})
          </Button>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default Branches;